import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import { LlmUsageApiService, LlmUsageFilters, LlmUsageRow } from 'src/app/core/services/llm-usage-api.service';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { ACTION_BUTTON } from 'src/app/shared/utils/action-button.classes';
import { CallDetailDrawerComponent } from '../llm-cost/call-detail-drawer/call-detail-drawer.component';
import { cleanFilters, formatUsd } from '../llm-cost/llm-cost.filters';
import { downloadCsv, toCsv } from '../llm-cost/csv-export.util';

/** Every LLM call, filterable. The row-level view behind every aggregate on the cost dashboard. */
@Component({
  selector: 'app-llm-usage',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PageSurfaceHeaderComponent, CallDetailDrawerComponent],
  templateUrl: './llm-usage.component.html',
})
export class LlmUsageComponent implements OnInit {
  readonly btn = ACTION_BUTTON;
  private readonly api = inject(LlmUsageApiService);
  private readonly route = inject(ActivatedRoute);

  readonly formatUsd = formatUsd;
  readonly statuses = ['SUCCESS', 'FAILED', 'UNKNOWN_BILLED'];
  readonly sources = ['PD', 'SIMULATE', 'PREVIEW', 'PROBE'];

  filters: LlmUsageFilters = { page: 1, limit: 25 };
  /** Unified PD search — sent as pd_id or session_code depending on the value shape. */
  pdSearch = '';
  rows: LlmUsageRow[] = [];
  meta = { total: 0, page: 1, limit: 25, pages: 0 };
  loading = false;
  error = '';
  openCallId: number | null = null;

  ngOnInit(): void {
    // Arriving from a dashboard drill-down: the tenant or PD is already chosen.
    const params = this.route.snapshot.queryParamMap;
    const tenantId = params.get('tenant_id');
    if (tenantId) this.filters.tenant_id = Number(tenantId);
    const sessionCode = params.get('session_code');
    const pdId = params.get('pd_id');
    this.pdSearch = pdId ?? sessionCode ?? '';
    const modelName = params.get('model_name');
    if (modelName) this.filters.model_name = modelName;
    this.syncPdSearchToFilters();
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .list(cleanFilters(this.filters))
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.rows = res.data ?? [];
          this.meta = res.meta ?? this.meta;
        },
        error: (err) => (this.error = (resolveApiErrorMessage(err) || 'Could not load LLM usage.')),
      });
  }

  applyFilters(): void {
    this.filters.page = 1;
    this.syncPdSearchToFilters();
    this.load();
  }

  resetFilters(): void {
    this.filters = { page: 1, limit: 25 };
    this.pdSearch = '';
    this.load();
  }

  private syncPdSearchToFilters(): void {
    delete this.filters.pd_id;
    delete this.filters.session_code;
    const query = this.pdSearch.trim();
    if (!query) return;
    if (/^PD-\d{6}$/i.test(query)) {
      this.filters.pd_id = query.toUpperCase();
      return;
    }
    this.filters.session_code = query;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.meta.pages) return;
    this.filters.page = page;
    this.load();
  }

  openCall(id: number): void {
    this.openCallId = id;
  }

  closeCall(): void {
    this.openCallId = null;
  }

  /**
   * Blank is treated as absent on purpose: tenant_name and tenant_code are both nullable AND
   * can be empty, and an empty string rendered as a tenant name is an invisible cell.
   */
  tenantLabel(row: LlmUsageRow): string {
    const name = row.tenant?.tenant_name?.trim();
    if (name) return name;

    const code = row.tenant_code?.trim();
    if (code) return code;

    return row.tenant_id === null ? 'Unattributed' : `Tenant ${row.tenant_id}`;
  }

  /**
   * Why a row has no tenant. Without this an unattributed call reads as a broken record rather
   * than what it is — operator or health traffic that legitimately belongs to no client.
   */
  unattributedReason(row: LlmUsageRow): string | null {
    if (row.tenant_id != null) return null;
    if (row.source === 'SIMULATE') return 'Operator simulate run — no client to charge.';
    if (row.source === 'PREVIEW') return 'Agent preview — no client to charge.';
    if (row.source === 'PROBE') return 'No tenant context reached the AI service for this call.';
    return null;
  }

  statusClass(status: string): string {
    if (status === 'SUCCESS') return 'badge bg-success-light text-success';
    if (status === 'FAILED') return 'badge bg-danger-light text-danger';
    return 'badge bg-warning-light text-warning';
  }

  /** Exports the page in view. Money goes out as the raw decimal string, not display text. */
  exportCsv(): void {
    const headers = [
      { key: 'pd_id', label: 'PD ID' },
      { key: 'request_timestamp', label: 'Date/Time' },
      { key: 'tenant', label: 'Tenant' },
      { key: 'session_code', label: 'PD Link' },
      { key: 'agent_id', label: 'Agent' },
      { key: 'task_code', label: 'Task' },
      { key: 'provider', label: 'Provider' },
      { key: 'model_name', label: 'Model' },
      { key: 'input_tokens', label: 'Input Tokens' },
      { key: 'output_tokens', label: 'Output Tokens' },
      { key: 'cached_tokens', label: 'Cached Tokens' },
      { key: 'total_tokens', label: 'Total Tokens' },
      { key: 'total_cost_usd', label: 'Cost (USD)' },
      { key: 'processing_time_ms', label: 'Processing (ms)' },
      { key: 'status', label: 'Status' },
    ];
    const rows = this.rows.map((row) => ({ ...row, tenant: this.tenantLabel(row) }));
    downloadCsv(`llm-usage-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(headers, rows));
  }
}
